import React, { useState } from "react";
import {
  Modal, Form, Select, Input, DatePicker, Button, message, Divider, Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { Api_Url } from "../../../config/config";

const { TextArea } = Input;
const { Option } = Select;

const getToken = () =>
  JSON.parse(sessionStorage.getItem("user"))?.accessToken;

const authHeader = () => ({
  Authorization: `Bearer ${getToken()}`,
});

const uploadAttachment = async (file) => {
  const formData = new FormData();
  formData.append("File", file);
  const res = await axios.post(
    `${Api_Url}api/v1/EmployeeGrievance/upload-attachment`,
    formData,
    { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }
  );
  return res?.data?.data?.url || null;
};

export const EmployeeGrievanceModalAdd = ({ onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const uploadProps = {
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      // File type validation
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'text/plain'
      ];
      
      const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.txt'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        message.error('Only PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, BMP, and TXT files are allowed!');
        return Upload.LIST_IGNORE;
      }
      
      // File size validation (10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        message.error('File size must be smaller than 10MB!');
        return Upload.LIST_IGNORE;
      }
      
      setFileList([file]);
      return false;
    },
    fileList,
    maxCount: 1,
    accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.txt',
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Upload attachment first if provided
      let attachmentUrl = null;
      if (fileList.length > 0) {
        attachmentUrl = await uploadAttachment(fileList[0]);
      }

      const payload = {
        Type: values.Type,
        EmployeeCode: values.EmployeeCode,
        EmployeeName: values.EmployeeName,
        ReceiptDate: values.ReceiptDate.format("YYYY-MM-DD"),
        ReceiptDetails: values.ReceiptDetails,
        HrRemarks: values.HrRemarks || null,
        // Date is system-generated on the server — no manual date sent
        AttachmentUrl: attachmentUrl,
      };

      const res = await fetch(`${Api_Url}api/v1/EmployeeGrievance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (res.ok) {
        message.success("Grievance created successfully");
        onSuccess();
      } else {
        throw new Error(result.message || "Failed to create grievance");
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: 18, fontWeight: "bold" }}>
          New Employee Grievance / Complaint
        </span>
      }
      open={true}
      onCancel={onClose}
      width={650}
      centered
      styles={{ body: { maxHeight: "70vh", overflowY: "auto", paddingRight: 8 } }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
        >
          Submit
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* Type */}
        <Form.Item
          name="Type"
          label="Type"
          rules={[{ required: true, message: "Please select type" }]}
        >
          <Select placeholder="Select type">
            <Option value="Complaint">Complaint</Option>
            <Option value="Grievance">Grievance</Option>
          </Select>
        </Form.Item>

        <Divider plain>Employee Details</Divider>

        <Form.Item
          name="EmployeeName"
          label="Employee Name"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="Full name" />
        </Form.Item>

        <Form.Item
          name="EmployeeCode"
          label="Employee ID"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="e.g. EMP001" />
        </Form.Item>

        <Divider plain>Receipt Information</Divider>

        <Form.Item
          name="ReceiptDate"
          label="Receipt Date"
          rules={[{ required: true, message: "Required" }]}
        >
          <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
        </Form.Item>

        <Form.Item
          name="ReceiptDetails"
          label="Receipt Detail"
          rules={[
            { required: true, message: "Required" },
            { min: 10, message: "Min 10 characters" },
          ]}
        >
          <TextArea rows={4} placeholder="Describe the complaint / grievance..." />
        </Form.Item>

        <Divider plain>HR Remarks</Divider>

        <Form.Item name="HrRemarks" label="HR Remarks">
          <TextArea rows={3} placeholder="Initial HR remarks..." />
        </Form.Item>

        {/* Date is system-generated — shown as info only */}
        <Form.Item label="Date">
          <Input
            value={new Date().toLocaleDateString("en-GB")}
            disabled
            style={{ color: "#888", background: "#f5f5f5" }}
          />
          <small style={{ color: "#aaa" }}>System generated</small>
        </Form.Item>

        <Form.Item label="Attachments">
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
